import { useTranslation } from 'react-i18next';

import type { ButtonProps } from '~/components/ui/button';
import { ArchiveToggleButton } from '~/features/list-archive/components/archive-toggle-button';

import { useSetExperimentArchived } from '../hooks/use-set-experiment-archived';

import { StopExperimentDialog } from './detail/stop-experiment-dialog';

interface ExperimentArchiveControlProps {
  experimentId: string;
  /** The row's current stamp — `archived` is derived from it. */
  archivedAt: string | null | undefined;
  buttonVariant?: ButtonProps['variant'];
  buttonSize?: ButtonProps['size'];
  showLabel?: boolean;
  className?: string;
}

/**
 * Archive / Unarchive for one experiment (list rows and the detail header): the
 * toggle, its mutation, and the Stop dialog an in-flight sub-run opens — after
 * a successful stop the archive is retried automatically. Owns its own mutation
 * instance so the table's column definitions stay memoizable.
 */
export function ExperimentArchiveControl({
  experimentId,
  archivedAt,
  buttonVariant,
  buttonSize,
  showLabel,
  className,
}: ExperimentArchiveControlProps) {
  const { t } = useTranslation('common');
  const { archive, unarchive, pendingId, refusal, dismissRefusal } = useSetExperimentArchived();
  return (
    <>
      <ArchiveToggleButton
        archived={archivedAt != null}
        isPending={pendingId === experimentId}
        onArchive={() => archive(experimentId)}
        onUnarchive={() => {
          unarchive(experimentId);
        }}
        {...(buttonVariant !== undefined ? { buttonVariant } : {})}
        {...(buttonSize !== undefined ? { buttonSize } : {})}
        {...(showLabel !== undefined ? { showLabel } : {})}
        {...(className !== undefined ? { className } : {})}
      />
      {refusal && (
        <StopExperimentDialog
          open
          onOpenChange={(open) => {
            if (!open) dismissRefusal();
          }}
          experimentId={refusal.id}
          title={t('listArchive.refusal.experimentTitle')}
          description={t('listArchive.refusal.experimentBody')}
          onStopped={() => {
            const { id } = refusal;
            dismissRefusal();
            // The owning hook handles the outcome (toast, or a fresh refusal).
            void archive(id).catch(() => undefined);
          }}
        />
      )}
    </>
  );
}
